import type {
  ActualWorkout,
  Discipline,
  WorkoutsResponse,
  WorkoutStatus,
} from '@/lib/workouts';

/**
 * Unidad común del calendario (M2.29): un entreno que aparece en una celda
 * Día/Semana/Mes con un ESTADO visible. Antes el calendario sólo mostraba
 * `actual` (entrenos hechos de Strava) y nunca pintaba los `planned` del
 * coach, así que el usuario no veía sus entrenos futuros y los completados
 * se confundían con los unplanned.
 *
 * Reglas de mapeo desde `WorkoutsResponse`:
 *
 *  1. Recorremos `planned`:
 *     - status='completed' + actualWorkoutId → kind='completed'.
 *     - status='partial'   + actualWorkoutId → kind='partial'.
 *     - status='skipped'                     → kind='skipped'.
 *     - status='planned'   (sin actual)      → kind='planned'.
 *     Si el planned tiene actualWorkoutId, lo registramos para luego no
 *     duplicar al iterar `actual`.
 *
 *  2. Recorremos `actual`: si su id NO está en la lista de "actualIds ya
 *     vinculados a planned" → kind='actual_unplanned' (vinieron de Strava
 *     sin plan previo).
 *
 *  3. Ordenamos cronológicamente.
 *
 * El componente `DayChip` (y los wrappers de Week/Day) pintan cada item
 * con un estilo distinto:
 *  - `completed` → fondo emerald (verde) + check ✓.
 *  - `planned`   → outline color disciplina, sin emoji.
 *  - `skipped`   → gris tachado.
 *  - `partial`   → ámbar + ½.
 *  - `actual_unplanned` → fondo lleno color disciplina (igual que antes
 *    para preservar la lectura habitual de Strava).
 */
export type CalendarItemKind =
  | 'planned'
  | 'completed'
  | 'partial'
  | 'skipped'
  // M2.41 — planned con datetime pasado SIN actual vinculado.
  // Visualmente rojo. Distinto de 'skipped' (acción explícita del usuario)
  // y de 'planned' (futuro pendiente).
  | 'missed'
  | 'actual_unplanned'
  // M2.34.C — comidas planificadas y prep tasks integradas en el calendario.
  | 'meal'
  | 'prep';

export interface CalendarItem {
  /** Clave estable para React (combina kind + id origen). */
  key: string;
  kind: CalendarItemKind;
  /**
   * Disciplina canónica para pintar color. Para `meal`/`prep` no aplica —
   * usamos un placeholder ('run' arbitrario) y el componente `DayChip` los
   * pinta con su propio color sin consultar este campo. Mantener `Discipline`
   * en lugar de hacerlo nullable evita romper los consumidores existentes.
   */
  discipline: Discipline;
  /** ISO 8601 cuando es el entreno (datetime del actual o planned). */
  datetime: string;
  timezone: string;
  /** Duración en minutos (actual.durationMin o planned.durationPlannedMin). */
  durationMin: number;
  /** Distancia en metros si la hay (sólo en actual). */
  distanceM: number | null;
  /** ID del actual asociado (si existe) — link a `/entreno/$id`. */
  actualId: number | null;
  /** ID del planned origen (si existe). */
  plannedId: number | null;
  /** TSS computado o estimado, o null. */
  tss: number | null;
  /** HR media (sólo actual). */
  avgHr: number | null;
  /** Status crudo del planned, o null si es actual_unplanned. */
  plannedStatus: WorkoutStatus | null;
  // ---- M2.34.C — campos opcionales para meal/prep ----
  /** Título legible (meal: nombre del plato, prep: "Preparar X"). */
  title?: string;
  /** Tipo de comida (breakfast, lunch, dinner...). Solo `kind=meal`. */
  mealType?: string;
  /** Receta vinculada si la hay (link a `/recetas/$id`). */
  recipeId?: number | null;
  /** Kcal de la comida. Solo `kind=meal`. */
  kcal?: number | null;
}

/**
 * Construye los items del calendario para UN día (key `YYYY-MM-DD`).
 * Devuelve la lista ya deduplicada y ordenada cronológicamente.
 *
 * M2.34.C — `extraItems` es opcional y se mergea al final del cálculo,
 * filtrado por `dayKey`. Permite que el padre inyecte meals/preps obtenidos
 * de `GET /api/calendar/items` sin tocar el flujo de workouts existente.
 * Cuando no se pasa nada, el comportamiento es idéntico al pre-M2.34.
 */
export function buildCalendarItemsForDay(
  workouts: WorkoutsResponse | null,
  dayKey: string,
  extraItems?: CalendarItem[],
): CalendarItem[] {
  if (!workouts && (!extraItems || extraItems.length === 0)) return [];
  if (!workouts) {
    const onlyExtras = (extraItems ?? []).filter((it) =>
      isOnDay(it.datetime, dayKey),
    );
    onlyExtras.sort((x, y) =>
      x.datetime < y.datetime ? -1 : x.datetime > y.datetime ? 1 : 0,
    );
    return onlyExtras;
  }

  const items: CalendarItem[] = [];
  const linkedActualIds = new Set<number>();

  // 1. Planned del día.
  for (const p of workouts.planned) {
    if (!isOnDay(p.datetime, dayKey)) continue;
    const linkedActual = findActual(workouts.actual, p.actualWorkoutId);
    if (p.actualWorkoutId !== null) linkedActualIds.add(p.actualWorkoutId);

    let kind: CalendarItemKind;
    if (p.status === 'completed') kind = 'completed';
    else if (p.status === 'partial') kind = 'partial';
    else if (p.status === 'skipped') kind = 'skipped';
    else {
      // M2.41 — Si datetime ya pasó (incluye hora) Y no hay actual
      // vinculado → 'missed' (rojo). Si es futuro o de hoy con hora
      // pendiente → 'planned' (outline).
      const plannedMs = Date.parse(p.datetime);
      const isPast =
        Number.isFinite(plannedMs) &&
        plannedMs < Date.now() &&
        p.actualWorkoutId === null;
      kind = isPast ? 'missed' : 'planned';
    }

    items.push({
      key: `p-${p.id}`,
      kind,
      discipline: p.discipline as Discipline,
      datetime: p.datetime,
      timezone: p.timezone,
      durationMin: linkedActual?.durationMin ?? p.durationPlannedMin,
      distanceM: linkedActual?.distanceM ?? null,
      actualId: linkedActual?.id ?? null,
      plannedId: p.id,
      tss:
        linkedActual?.tssComputed ??
        linkedActual?.tssEstimated ??
        null,
      avgHr: linkedActual?.avgHr ?? null,
      plannedStatus: p.status,
    });
  }

  // 2. Actuals del día NO vinculados a ningún planned.
  for (const a of workouts.actual) {
    if (!isOnDay(a.datetime, dayKey)) continue;
    if (linkedActualIds.has(a.id)) continue;
    items.push({
      key: `a-${a.id}`,
      kind: 'actual_unplanned',
      discipline: a.discipline as Discipline,
      datetime: a.datetime,
      timezone: a.timezone,
      durationMin: a.durationMin,
      distanceM: a.distanceM ?? null,
      actualId: a.id,
      plannedId: null,
      tss: a.tssComputed ?? a.tssEstimated ?? null,
      avgHr: a.avgHr ?? null,
      plannedStatus: null,
    });
  }

  // M2.34.C — extras (meals/preps) filtrados por el mismo día.
  if (extraItems && extraItems.length > 0) {
    for (const ex of extraItems) {
      if (!isOnDay(ex.datetime, dayKey)) continue;
      items.push(ex);
    }
  }

  items.sort((x, y) =>
    x.datetime < y.datetime ? -1 : x.datetime > y.datetime ? 1 : 0,
  );
  return items;
}

/**
 * M2.34.C — Convierte el DTO de `GET /api/calendar/items` (kind='meal'|'prep')
 * a un `CalendarItem` que `DayChip`/`DayView` pueden pintar. Sólo aplica a
 * los kinds nuevos: workout-planned/workout-actual ya los proporciona
 * `useWorkoutsInRange` y se construyen vía `buildCalendarItemsForDay`.
 */
export interface UnifiedCalendarApiItem {
  kind: 'workout-planned' | 'workout-actual' | 'meal' | 'prep';
  id: string;
  datetime: string;
  durationMin: number;
  title: string;
  discipline?: string;
  mealType?: string;
  recipeId?: number | null;
  mealPlanItemId?: number;
  kcal?: number | null;
  proteinG?: number | null;
  carbsG?: number | null;
  fatG?: number | null;
  prepRecipeId?: number;
}

export function unifiedItemsToMealAndPrepCalendarItems(
  items: UnifiedCalendarApiItem[],
  fallbackTimezone = 'Europe/Madrid',
): CalendarItem[] {
  const out: CalendarItem[] = [];
  for (const it of items) {
    if (it.kind !== 'meal' && it.kind !== 'prep') continue;
    out.push({
      key: it.id,
      kind: it.kind,
      // Placeholder de disciplina — `DayChip` no la usa para meal/prep.
      discipline: 'run' as Discipline,
      datetime: it.datetime,
      timezone: fallbackTimezone,
      durationMin: it.durationMin,
      distanceM: null,
      actualId: null,
      plannedId: null,
      tss: null,
      avgHr: null,
      plannedStatus: null,
      title: it.title,
      ...(it.mealType !== undefined && { mealType: it.mealType }),
      ...(it.recipeId !== undefined && { recipeId: it.recipeId }),
      ...(it.kcal !== undefined && { kcal: it.kcal }),
    });
  }
  return out;
}

function findActual(
  arr: ActualWorkout[],
  id: number | null,
): ActualWorkout | null {
  if (id === null) return null;
  return arr.find((a) => a.id === id) ?? null;
}

function isOnDay(iso: string, dayKey: string): boolean {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return false;
  return iso.slice(0, 10) === dayKey || sameDayUTC(d, dayKey);
}

function sameDayUTC(d: Date, dayKey: string): boolean {
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}` === dayKey;
}

// ---------------------------------------------------------------------------
// Estilos por estado
// ---------------------------------------------------------------------------

export interface CalendarItemStyle {
  /** Color de fondo (inline style, viene del DISCIPLINE_COLORS para
   *  unplanned/completed; tailwind para skipped/partial). */
  background: string;
  /** Color de texto / tailwind class. */
  textClass: string;
  /** Borde extra (border-l-4 con color de disciplina, etc.). */
  borderClass: string;
  /** Prefijo iconográfico (✓ · ✗ · ½ · etc.) o cadena vacía. */
  prefix: string;
  /** Opacidad o text-decoration extra. */
  extraClass: string;
}
